import type {
  SitePrivateConfig,
  RecordEntityId,
  SitePublicConfig,
  CognitionGroups,
} from '@centric-os/types';
import type { GetCentricosSiteClientSettingsResponse } from '@compassdigital/sdk.typescript/interface/centricos';
import { defineStore, type StateTree } from 'pinia';
import { fromLegacySitePrivateConfig, toLegacySitePrivateConfig } from '../mappers/private-config';
import { useTypedConfigRoutes } from '../typed-config-routes';

const COGNITION_CONFIG_KEY = 'cognition';

/**
 * Interface for the state of the `useSiteConfigStore`.
 */
interface State extends StateTree {
  sitePublicConfig: SitePublicConfig | null;
  sitePrivateConfig: SitePrivateConfig | null;
  siteCognitionIds: CognitionGroups | null;
  clientSettings: GetCentricosSiteClientSettingsResponse | null;
}

/**
 * Pinia store for managing the site configuration.
 * This store handles fetching and saving public/private site configuration data and cognition groups.
 *
 * @param {string} [storeId='site-configuration'] - The unique ID of the store.
 * @returns {import('pinia').Store} The Pinia store instance for site configuration.
 */
export function useSiteConfigStore(storeId = 'site-configuration') {
  const store = defineStore(storeId, {
    state: (): State => ({
      sitePublicConfig: null,
      sitePrivateConfig: null,
      siteCognitionIds: null,
      clientSettings: null,
    }),
    actions: {
      /**
       * Fetches the public configuration for a specific site.
       *
       * @param {RecordEntityId} siteId - The ID of the site to fetch the public configuration for.
       * @returns {Promise<void>} A promise that resolves once the public configuration is fetched and stored in the state.
       */
      async getSitePublicConfig(siteId: RecordEntityId): Promise<void> {
        const response = useTypedConfigRoutes()
          ? await this.serviceApi.get_config_site_public(String(siteId))
          : await this.serviceApi.get_config_public(String(siteId));

        this.sitePublicConfig = response as SitePublicConfig;
      },

      /**
       * Fetches the private configuration for a specific site.
       *
       * @param {RecordEntityId} siteId - The ID of the site to fetch the private configuration for.
       * @returns {Promise<void>} A promise that resolves once the private configuration is fetched and stored in the state.
       */
      async getSitePrivateConfig(siteId: RecordEntityId): Promise<void> {
        const response = useTypedConfigRoutes()
          ? await this.serviceApi.get_config_site_private(String(siteId))
          : await this.serviceApi.get_config(String(siteId));
        this.sitePrivateConfig = toLegacySitePrivateConfig(response);
      },

      /**
       * Fetches both public and private configurations for a specific site concurrently.
       * Uses `Promise.all` to fetch both configurations in parallel.
       *
       * @param {RecordEntityId} siteId - The ID of the site to fetch both public and private configurations for.
       * @returns {Promise<void>} A promise that resolves once both public and private configurations are fetched and stored.
       */
      async fetchSiteConfigurations(siteId: RecordEntityId): Promise<void> {
        const publicConfig = this.getSitePublicConfig(siteId);
        const privateConfig = this.getSitePrivateConfig(siteId);
        await Promise.all([publicConfig, privateConfig]);
      },

      /**
       * Fetches the read-only settings (access code, aggregate view) of the site's client.
       * They are informational, so a failure leaves them empty instead of breaking Site Settings.
       *
       * @param {RecordEntityId} siteId - The site whose client settings to fetch.
       * @returns {Promise<void>} A promise that resolves once the client settings are stored in the state.
       */
      async getClientSettings(siteId: RecordEntityId): Promise<void> {
        this.clientSettings = null;
        try {
          this.clientSettings = await this.serviceApi.get_centricos_site_client_settings(
            String(siteId),
          );
        } catch (error) {
          console.error('Error fetching client settings:', error);
        }
      },

      /**
       * Fetches cognition groups for all sites.
       * Note: The current API fetches cognition groups globally across sites, not targeting a specific site.
       * This will need to be updated once the API supports site-specific cognition groups.
       *
       * @returns {Promise<void>} A promise that resolves once the cognition groups are fetched and stored in the state.
       */
      async getSitesCognition(): Promise<void> {
        const response = await this.serviceApi.get_config_public(COGNITION_CONFIG_KEY);
        this.siteCognitionIds = response as CognitionGroups;
      },

      /**
       * Saves the cognition groups for all sites.
       * Note: The current API saves cognition groups globally across sites, not targeting a specific site.
       * This will need to be updated once the API supports site-specific cognition groups.
       *
       * @param {CognitionGroups} payload - The cognition groups to be saved.
       * @returns {Promise<void>} A promise that resolves once the cognition groups are successfully saved.
       */
      async saveSitesCognition(payload: CognitionGroups): Promise<void> {
        const response = await this.serviceApi.post_config_public(COGNITION_CONFIG_KEY, {
          ...payload,
        });
        this.siteCognitionIds = response as CognitionGroups;
      },

      /**
       * Saves the private configuration for a specific site.
       *
       * @param {string} id - The ID of the site whose private configuration is being saved.
       * @param {Partial<SitePrivateConfig>} payload - A partial object representing the updated private configuration.
       * @returns {Promise<void>} A promise that resolves once the private configuration is successfully saved.
       */
      async savePrivate(id: string, payload: Partial<SitePrivateConfig>): Promise<void> {
        if (useTypedConfigRoutes()) {
          await this.serviceApi.put_config_site_private(id, fromLegacySitePrivateConfig(payload));
        } else {
          await this.serviceApi.post_config(id, { ...payload });
        }
      },

      /**
       * Saves the public configuration for a specific site.
       *
       * @param {string} id - The ID of the site whose public configuration is being saved.
       * @param {Partial<SitePublicConfig>} payload - A partial object representing the updated public configuration.
       * @returns {Promise<void>} A promise that resolves once the public configuration is successfully saved.
       */
      async savePublic(id: string, payload: Partial<SitePublicConfig>): Promise<void> {
        if (useTypedConfigRoutes()) {
          await this.serviceApi.put_config_site_public(id, payload);
        } else {
          await this.serviceApi.post_config_public(id, { ...payload });
        }
      },
    },
    pagination: false,
  });

  return store();
}
